import {Box, Flex, Image, showMessage, Text, Title} from 'exsportia-components'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import styled from 'styled-components'

import {PromoError} from '../../api/promo-codes/types'
import {storeQuoteQueryKey, useStoreCheckout, useStoreGoods, useStoreQuote} from '../../api/store'
import {cartPromoView} from '../../api/store/cart-promo'
import {useVenue} from '../../api/venue/hooks'
import {handlePromoPayError} from '../../components/promo-code-field/handle-promo-pay-error'
import {PromoCodeField} from '../../components/promo-code-field/promo-code-field'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {useStoreCart} from '../../services/store/cart'
import {theme} from '../../theme/theme'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {ProductPlaceholder} from './store-page'
import {useCurrencyFormat} from './store-shared'

// 40px square: a real touch target, and wide enough that the glyph is centred.

const PriceRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
`

const Was = styled.span`
  color: rgba(0, 16, 61, 0.48);
  text-decoration: line-through;
`

const Now = styled.span`
  color: ${theme.colors.primary};
  font-weight: 600;
`

const PromoTag = styled.span`
  padding: 1px 6px;
  border-radius: 6px;
  background: ${theme.colors['primary-secondary']};
  color: ${theme.colors.primary};
  font-size: 12px;
  font-weight: 600;
`

const Muted = styled.span`
  font-size: 12px;
  color: rgba(0, 16, 61, 0.48);
`

const STEPPER_BUTTON = {width: '40px', height: '40px', p: 0}

const PaymentChoice: FC<{selected: boolean; label: string; hint?: string; onClick: () => void}> = ({
  selected,
  label,
  hint,
  onClick,
}) => (
  <Flex
    p='12px'
    gap='10px'
    cursor='pointer'
    borderRadius='12px'
    alignItems='flex-start'
    onClick={onClick}
    border={selected ? '2px solid #EC1E5F' : '1px solid rgba(0, 16, 61, 0.12)'}
    backgroundColor={selected ? 'rgba(236, 30, 95, 0.04)' : 'transparent'}
  >
    <Flex
      mt='2px'
      width='18px'
      height='18px'
      flexShrink={0}
      borderRadius='50%'
      alignItems='center'
      justifyContent='center'
      border={selected ? '5px solid #EC1E5F' : '1.5px solid rgba(0, 16, 61, 0.24)'}
    />
    <Flex flexDirection='column'>
      <Text
        text={label}
        settings={{fontWeight: 600}}
      />
      {hint && (
        <Text
          text={hint}
          settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
        />
      )}
    </Flex>
  </Flex>
)

type OutOfStockDetail = {
  name?: string
  variantLabel?: string | null
  available?: number
  requested?: number
}

export const StoreCartPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const format = useCurrencyFormat()
  const {venueId} = useParams<{venueId: string}>()
  const cart = useStoreCart(venueId)
  // Venue store switches ride on the catalogue response (WBA-4538).
  const {data: goods} = useStoreGoods(venueId)
  const {data: venue} = useVenue()
  const allowPayOnLocation = goods?.settings?.payOnLocation !== false
  const {isLoggedIn, signIn} = useAuthState(venueId)
  const checkout = useStoreCheckout()
  const [payOnLocation, setPayOnLocation] = useState(false)
  const [confirmedReference, setConfirmedReference] = useState<string | null>(null)
  const [promoCode, setPromoCode] = useState<string | null>(null)
  const [promoRejection, setPromoRejection] = useState<PromoError | null>(null)

  const checkoutLines = useMemo(
    () =>
      cart.lines.map((line) => ({
        stockItemId: line.stockItemId,
        ...(line.variantId ? {variantId: line.variantId} : {}),
        quantity: line.quantity,
      })),
    [cart.lines],
  )
  const promoItems = useMemo(
    () =>
      Array.from(new Set(cart.lines.map((line) => line.stockItemId))).map((entityId) => ({
        entityType: 'product' as const,
        entityId,
      })),
    [cart.lines],
  )
  const quote = useStoreQuote(venueId, {lines: checkoutLines, promoCode: promoCode || undefined})
  const quoteData = promoCode ? quote.data : undefined
  const promoApplied = quoteData?.promoApplied ?? null
  const promoDiscount = promoApplied ? quoteData?.promoDiscount || 0 : 0
  const total = promoApplied && quoteData ? quoteData.totalPrice : cart.total
  // Per line, what the code covers (server #3633). Without it (an older server) the cart shows only the summary.
  const promoView = useMemo(
    () => cartPromoView(cart.lines, promoCode ? quoteData?.lines : undefined, promoApplied),
    [cart.lines, promoCode, quoteData?.lines, promoApplied],
  )
  const promoOn =
    promoView.coveredNames.length > 2
      ? l('promo_n_items', {n: promoView.coveredNames.length})
      : promoView.coveredNames.join(', ')

  useEffect(() => {
    if (promoCode && !quote.isFetching && quoteData?.promoError) {
      setPromoRejection(quoteData.promoError)
      setPromoCode(null)
    }
  }, [promoCode, quote.isFetching, quoteData?.promoError])

  const applyPromo = useCallback((code: string) => {
    setPromoRejection(null)
    setPromoCode(code)
  }, [])
  const removePromo = useCallback(() => {
    setPromoRejection(null)
    setPromoCode(null)
  }, [])
  const setPromoField = useCallback((field: string, value: unknown) => {
    if (field === 'promoCode') {
      setPromoCode(value as string | null)
    }
    if (field === 'promoRejection') {
      setPromoRejection(value as PromoError | null)
    }
  }, [])

  const submit = () => {
    if (!venueId || cart.lines.length === 0 || checkout.isLoading) {
      return
    }
    checkout.mutate(
      {
        venueId,
        // never submit a method the venue has switched off, even if it was
        // selected before the settings arrived
        payOnLocation: payOnLocation && allowPayOnLocation,
        lines: checkoutLines,
        ...(promoCode && promoApplied ? {promoCode} : {}),
      },
      {
        onSuccess: (result) => {
          // WBA-4570: checkout only creates the sale DRAFT. Clearing here emptied the cart before
          // a single baht moved, so backing out of the payment page lost the whole basket and the
          // customer had to rebuild it. Pay-on-location is the one branch where the order really
          // is placed at this point; the online branch clears on the payment-success page instead.
          if (result.payOnLocation) {
            cart.clear()
            setConfirmedReference(result.reference || result.entityId)
            return
          }
          navigate(`/clear-view/${venueId}/payment?entityId=${result.entityId}&entity=sale`)
        },
        onError: (error) => {
          if (handlePromoPayError(error, setPromoField, storeQuoteQueryKey)) {
            return
          }
          const data = error.response?.data
          if (data?.type === 'OUT_OF_STOCK' && Array.isArray(data.details)) {
            const lines = (data.details as OutOfStockDetail[]).map(
              (detail) =>
                `${detail.name || ''}${detail.variantLabel ? ` (${detail.variantLabel})` : ''}: ${
                  detail.available ?? 0
                }`,
            )
            showMessage(`${l('store-out-of-stock', {caseAction: 'capitalize'})} — ${lines.join('; ')}`, 'error')
            return
          }
          showMessage(data?.message || l('error'), 'error')
        },
      },
    )
  }

  if (confirmedReference) {
    return (
      <GridLayout>
        <Flex
          px='16px'
          py='48px'
          gap='16px'
          width='100%'
          alignItems='center'
          flexDirection='column'
        >
          <Title
            styleType='h1'
            text={l('store-order-confirmed', {caseAction: 'capitalize'})}
          />
          <Text
            text={`${l('store-order', {caseAction: 'capitalize'})} ${confirmedReference}`}
            settings={{fontWeight: 600}}
          />
          <Text
            text={l('store-pickup-note', {caseAction: 'capitalize'})}
            settings={{color: 'rgba(0, 16, 61, 0.48)', textAlign: 'center'}}
          />
          <Button
            type='primary'
            onClick={() => navigate('../orders')}
          >
            {l('store-view-orders', {caseAction: 'capitalize'})}
          </Button>
          <Button
            type='secondary'
            onClick={() => navigate(`/${venueId}`)}
          >
            {l('store-back-to-venue', {caseAction: 'capitalize'})}
          </Button>
        </Flex>
      </GridLayout>
    )
  }

  return (
    <GridLayout
      header={
        <Flex p='16px'>
          <Title text={l('store-cart-title', {caseAction: 'capitalize'})} />
        </Flex>
      }
    >
      {/* Box a: the lines. */}
      <Box width='100%'>
        {cart.lines.length === 0 && (
          <Flex
            py='48px'
            gap='16px'
            alignItems='center'
            flexDirection='column'
          >
            <Text
              text={l('store-cart-empty', {caseAction: 'capitalize'})}
              settings={{color: 'rgba(0, 16, 61, 0.48)'}}
            />
            <Button
              type='secondary'
              onClick={() => navigate('../store')}
            >
              {l('store-title', {caseAction: 'capitalize'})}
            </Button>
          </Flex>
        )}
        {cart.lines.map((line, index) => {
          const key = cart.lineKey(line)
          const promoLine = promoView.lines[index]
          const covered = Boolean(promoLine?.covered)
          return (
            <Flex
              key={key}
              py='12px'
              gap='12px'
              alignItems='center'
            >
              <Box
                width='56px'
                height='56px'
                flexShrink={0}
                borderRadius='12px'
                overflow='hidden'
                backgroundColor='rgba(0, 16, 61, 0.06)'
              >
                {line.thumbUrl ? (
                  <Image
                    width='100%'
                    height='100%'
                    style={{objectFit: 'cover'}}
                    src={line.thumbUrl}
                  />
                ) : (
                  <ProductPlaceholder size={20} />
                )}
              </Box>
              <Flex
                flex='1'
                minWidth='0'
                flexDirection='column'
              >
                <Text text={line.name} />
                {covered && promoLine ? (
                  <PriceRow>
                    <Was>{format(line.price * line.quantity)}</Was>
                    <Now>{format(promoLine.payableAfter)}</Now>
                    <PromoTag>{promoView.tag ?? `−${format(promoLine.promoDiscount)}`}</PromoTag>
                    {line.variantLabel && <Muted>· {line.variantLabel}</Muted>}
                  </PriceRow>
                ) : (
                  <Text
                    text={`${format(line.price)}${line.variantLabel ? ` · ${line.variantLabel}` : ''}`}
                    settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
                  />
                )}
                {promoLine && !covered && <Muted>{l('promo_not_in')}</Muted>}
              </Flex>
              {/* A bare `icon` button is padding-less and max-content wide, which
                left a ~12px tap target on touch — these are sized properly. */}
              <Flex
                gap='4px'
                flexShrink={0}
                alignItems='center'
              >
                <Button
                  type='secondary'
                  settings={STEPPER_BUTTON}
                  onClick={() => cart.setQuantity(key, line.quantity - 1)}
                >
                  −
                </Button>
                <Flex
                  width='36px'
                  justifyContent='center'
                >
                  <Text
                    text={String(line.quantity)}
                    settings={{fontWeight: 600}}
                  />
                </Flex>
                <Button
                  type='secondary'
                  settings={STEPPER_BUTTON}
                  onClick={() => cart.setQuantity(key, line.quantity + 1)}
                >
                  +
                </Button>
              </Flex>
            </Flex>
          )
        })}
      </Box>
      {/* Box b: pay-on-location, total and checkout (side column on desktop). */}
      <Box
        width='100%'
        p='16px'
      >
        {cart.lines.length > 0 && (
          <>
            {/* Paying now and paying at pickup are two choices, not a yes/no
              question about one of them (Dima 2026-08-17). */}
            <Box pt='8px'>
              <Text
                text={l('store-payment-method', {caseAction: 'capitalize'})}
                settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
              />
            </Box>
            <Flex
              mt='8px'
              gap='8px'
              flexDirection='column'
            >
              <PaymentChoice
                selected={!payOnLocation}
                label={l('store-pay-now', {caseAction: 'capitalize'})}
                onClick={() => setPayOnLocation(false)}
              />
              {allowPayOnLocation && (
                <PaymentChoice
                  selected={payOnLocation}
                  label={l('store-pay-on-location', {caseAction: 'capitalize'})}
                  hint={l('store-pickup-note', {caseAction: 'capitalize'})}
                  onClick={() => setPayOnLocation(true)}
                />
              )}
            </Flex>
            <Box mt='8px'>
              <PromoCodeField
                venueId={venueId}
                items={promoItems}
                code={promoCode}
                applied={promoApplied}
                rejection={promoRejection}
                isQuoting={quote.isFetching}
                currency={quoteData?.currency || venue?.currency}
                hidden={cart.total === 0}
                onApply={applyPromo}
                onRemove={removePromo}
              />
            </Box>
            {promoApplied && promoDiscount > 0 && (
              <Flex
                pt='8px'
                alignItems='baseline'
                justifyContent='space-between'
              >
                <Text
                  text={
                    promoOn
                      ? l('promo_on_items', {code: promoApplied.code, items: promoOn})
                      : l('promo_line', {code: promoApplied.code})
                  }
                  settings={{color: 'primary', fontWeight: 600}}
                />
                <Text
                  text={`-${format(promoDiscount)}`}
                  settings={{color: 'primary', fontWeight: 600}}
                />
              </Flex>
            )}
            <Flex
              mt='16px'
              pt='16px'
              alignItems='baseline'
              justifyContent='space-between'
              borderTop='1px solid rgba(0, 16, 61, 0.08)'
            >
              <Title
                styleType='h3'
                text={l('store-total', {caseAction: 'capitalize'})}
              />
              <Title
                styleType='h2'
                text={format(total)}
              />
            </Flex>
            <Box pb='12px' />
            {/* An order needs an owner: order reads are owner-only and status
              updates are pushed by userId, so a signed-out order would be
              unreachable by the customer and uncontactable by the venue. */}
            {isLoggedIn ? (
              <Button
                type='primary'
                isLoading={checkout.isLoading}
                onClick={submit}
              >
                {payOnLocation
                  ? l('store-place-order', {caseAction: 'capitalize'})
                  : l('store-pay-now', {caseAction: 'capitalize'})}
              </Button>
            ) : (
              <>
                <Box pb='8px'>
                  <Text
                    text={l('store-signin-order', {caseAction: 'capitalize'})}
                    settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
                  />
                </Box>
                <Button
                  type='primary'
                  onClick={() => signIn()}
                >
                  {l('signin', {caseAction: 'capitalize'})}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </GridLayout>
  )
}
